import { type ReactNode } from 'react'
import { Dialog } from './Dialog.tsx'
import styles from './FilterDialog.module.css'

type Option<T> = {
	value: T
	label: string
	emoji?: string
}

type Props<T> = {
	open: boolean
	title: string
	options: ReadonlyArray<Option<T>>
	selected: ReadonlyArray<T>
	onToggle: (value: T) => void
	onClear: () => void
	onClose: () => void
	footer?: ReactNode
}

// Generic pill multi-select dialog: tap a pill to toggle, "Clear" wipes the
// selection, "Done" closes. Shared by the activity and recipe filter bars.
export const FilterDialog = <T extends string>({
	open,
	title,
	options,
	selected,
	onToggle,
	onClear,
	onClose,
	footer,
}: Props<T>) => {
	return (
		<Dialog open={open} onClose={onClose}>
			<div className={styles.panel}>
				<div className={styles.headerRow}>
					<h2 className={styles.heading}>{title}</h2>
					<button
						type="button"
						className={styles.clear}
						onClick={onClear}
						disabled={selected.length === 0}
					>
						Clear
					</button>
				</div>

				<div className={styles.pills}>
					{options.map((opt) => {
						const active = selected.includes(opt.value)
						return (
							<button
								key={opt.value}
								type="button"
								className={`${styles.pill} ${active ? styles.pillActive : ''}`.trim()}
								aria-pressed={active}
								onClick={() => onToggle(opt.value)}
							>
								{opt.emoji ? (
									<span aria-hidden="true">{opt.emoji}</span>
								) : null}{' '}
								{opt.label}
							</button>
						)
					})}
				</div>

				{footer}

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.done}
						onClick={onClose}
					>
						Done
					</button>
				</div>
			</div>
		</Dialog>
	)
}
